import cn from 'classnames';
import { useCustomer } from '@adapters/bigcommerce/hooks/customer/index';

import styles from './PriceContainer.module.scss';

export interface PriceContainerProps {
  price: string | null;
  originalPrice?: string | null;
  discountLabel?: string | null;
  isMemberDiscount?: boolean;
  salePriceAriaLabel?: string;
  className?: string;
  /** PDP: in-cart discount tag styling on the current price line */
  hasInCartDiscountTag?: boolean;
  /** PDP: member / staff group discounted price emphasis */
  hasMemberGroupPrice?: boolean;
  priceTestId?: string;
  originalPriceTestId?: string;
  /** Sticky ATC bar: single-line, body-small prices with the original price inline */
  isCompact?: boolean;
}

const PriceContainer = ({
  price,
  originalPrice,
  discountLabel,
  isMemberDiscount = false,
  salePriceAriaLabel,
  className,
  hasInCartDiscountTag = false,
  hasMemberGroupPrice = false,
  priceTestId = 'actual-price',
  originalPriceTestId = 'original-price',
  isCompact = false,
}: PriceContainerProps) => {
  const { customerIsLoggedIn } = useCustomer();
  const hasOriginalPrice = Boolean(originalPrice) && originalPrice !== price;
  const hasDiscountLabel = Boolean(discountLabel) && hasOriginalPrice;

  if (!price) {
    return null;
  }

  return (
    <div
      className={cn(
        styles.priceContainer,
        { [styles.compact]: isCompact },
        className
      )}
    >
      <p
        className={cn(styles.actualPrice, {
          [styles.actualPriceInCartDiscount]: hasInCartDiscountTag,
          [styles.actualPriceMemberGroup]:
            customerIsLoggedIn && (hasMemberGroupPrice || isMemberDiscount),
        })}
        data-testid={priceTestId}
      >
        {price}
      </p>
      {hasOriginalPrice && (
        <div className={styles.discountRow} data-testid="sale-price-block">
          {salePriceAriaLabel && (
            <span className={styles.srOnly}>{salePriceAriaLabel}</span>
          )}
          <s className={styles.originalPrice} data-testid={originalPriceTestId}>
            {originalPrice}
          </s>
          {hasDiscountLabel && (
            <p
              className={cn(styles.discountLabel, {
                [styles.memberDiscountLabel]: isMemberDiscount,
              })}
            >
              {discountLabel}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceContainer;
